import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { supabase } from '@/lib/supabase';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { useOptimizedGeolocation } from '@/hooks/useOptimizedGeolocation';
import {
  MapPin,
  AlertCircle,
  CheckCircle2,
  HelpCircle,
  ArrowRight,
  X,
  Plus,
  ArrowLeft,
  Camera,
  Image,
  Lock
} from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';

const STEPS = {
  LOCATION: 'location',
  TERRAIN_NAME: 'terrain_name',
  PHOTOS: 'photos',
  SPOT_CHECK: 'spot_check',
  AI_ANALYSIS: 'ai_analysis',
  AI_UNCERTAIN: 'ai_uncertain',
  REVIEW: 'review',
  POST_CREATE: 'post_create',
  SUCCESS: 'success'
};

export default function AddPlayground({ onClose }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    zip: '',
    type: 'outdoor',
    meta_json: {}
  });

  const [location, setLocation] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [step, setStep] = useState(STEPS.LOCATION);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Spot detection
  const [spotData, setSpotData] = useState(null);

  const [createdTerrain, setCreatedTerrain] = useState(null);
  const [canAddMore, setCanAddMore] = useState(false);

  const { getLocation } = useOptimizedGeolocation();

  // AI analysis
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);


  // Auto-scroll to bottom on mobile when step changes
  useEffect(() => {
    if (window.innerWidth < 768) {
      setTimeout(() => {
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
      }, 100);
    }
  }, [step]);

  // Scroll input into view when focused (for mobile keyboard)
  useEffect(() => {
    const handleFocus = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        setTimeout(() => {
          e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }
    };

    document.addEventListener('focus', handleFocus, true);
    return () => document.removeEventListener('focus', handleFocus, true);
  }, []);

  if (!user) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#0A0B10]">
        <Card className="p-8 bg-[#13151D] border-[#6E2BFF]">
          <p className="text-white mb-4">Veuillez vous connecter pour ajouter un terrain.</p>
          <Button
            onClick={() => navigate('/login')}
            className="bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white"
          >
            Connexion
          </Button>
        </Card>
      </div>
    );
  }

  // Level gate — minimum level 3 to create courts (admin-code users bypass)
  const userLevel = user.level || 1;
  if (userLevel < 3 && !user.admin_code_user && user.role !== 'admin') {
    return (
      <div className="p-6 text-center">
        {onClose && (
          <div className="flex justify-end mb-4">
            <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:text-[#6E2BFF]">
              <X className="w-5 h-5" />
            </Button>
          </div>
        )}
        <Lock className="w-12 h-12 text-[#6E2BFF] mx-auto mb-4" />
        <h2 className="text-xl font-black text-white mb-2">Niveau 3 requis</h2>
        <p className="text-[#B3B6C2] text-sm mb-4">
          Tu pourras créer des terrains à partir du niveau 3.
          Continue à jouer pour monter de niveau !
        </p>
        <div className="bg-[#1B1E28] rounded-lg p-3 inline-block">
          <p className="text-xs text-[#B3B6C2]">Ton niveau actuel</p>
          <p className="text-2xl font-black text-[#6E2BFF]">N{userLevel}</p>
        </div>
        <div className="mt-4 text-xs text-[#B3B6C2] space-y-1">
          <p>N3–4 : 1 terrain</p>
          <p>N5–6 : 2 terrains</p>
          <p>N7 : 3 terrains</p>
          <p>N8+ : 4 terrains</p>
        </div>
      </div>
    );
  }

  // Step: Location
  const handleLocationSubmit = async () => {
    if (!location) {
      setError('Veuillez sélectionner une localisation');
      return;
    }

    setError('');
    setStep(STEPS.TERRAIN_NAME);

    // Detect spot in background
    Promise.resolve({
      spot_id: `spot_${Math.round(location.lat * 1000)}_${Math.round(location.lng * 1000)}`,
      courts_count: 0
    }).then(data => {
      setSpotData(data);
    }).catch(err => {
      console.error('Failed to detect spot:', err);
    });
  };

  const handleSpotCheckContinue = () => {
    if (spotData.courts_count >= 4) {
      setError('');
      setStep(STEPS.TERRAIN_NAME);
      return;
    }
    setStep(STEPS.TERRAIN_NAME);
  };

  // Step: Photos (2 required, 3rd optional for AI)
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setLoading(true);
    try {
      for (const file of files) {
        if (photos.length >= 3) {
          setError('Maximum 3 photos autorisées');
          break;
        }

        const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
        const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 8)}.${ext}`;
        console.log('Uploading:', fileName, 'size:', file.size, 'type:', file.type);
        const { error: uploadError } = await supabase.storage
          .from('playground-photos')
          .upload(fileName, file, { contentType: file.type || 'image/jpeg' });
        if (uploadError) {
          console.error('Upload error:', uploadError);
          throw uploadError;
        }

        const { data: { publicUrl } } = supabase.storage
          .from('playground-photos')
          .getPublicUrl(fileName);

        setPhotos((prev) => [...prev, publicUrl]);
      }
    } catch (err) {
      setError('Échec de l\'upload des photos');
    } finally {
      setLoading(false);
    }
  };

  const removePhoto = (index) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const handlePhotosSubmit = async () => {
    if (photos.length < 2) {
      setError('Minimum 2 photos requises');
      return;
    }

    // Always run AI analysis
    setLoading(true);
    setAiLoading(true);
    try {
      const response = { data: { result: 'NOT_ANALYZED' } };

      setAiResult(response.data);

      // If MATCH_SAME_COURT but internal (new court photos are coherent), continue to review
      if (response.data.result === 'MATCH_SAME_COURT' && response.data.analysis_type === 'internal') {
        setStep(STEPS.REVIEW);
      } else if (response.data.result === 'MATCH_SAME_COURT' && response.data.analysis_type === 'external') {
        // External match = duplicate with existing court
        setStep(STEPS.AI_ANALYSIS);
      } else if (response.data.result === 'UNCERTAIN') {
        setStep(STEPS.AI_UNCERTAIN);
      } else {
        // DIFFERENT_COURT_SAME_AREA
        setStep(STEPS.REVIEW);
      }
    } catch (err) {
      setError(err.message || 'Échec de l\'analyse IA');
    } finally {
      setLoading(false);
      setAiLoading(false);
    }
  };

  // Step: AI Uncertain - ask for 3rd photo or user choice
  const handleAiUncertainAddPhoto = () => {
    // Reset to photos step to add 3rd photo
    setAiResult(null);
    setStep(STEPS.PHOTOS);
  };

  const handleAiUncertainUserChoice = async (choice) => {
    // User chooses SAME or DIFFERENT
    if (choice === 'SAME') {
      // Show list of existing courts
      setStep(STEPS.AI_ANALYSIS);
    } else {
      // Continue to review (different court)
      setAiResult({ ...aiResult, result: 'DIFFERENT_COURT_SAME_AREA' });
      setStep(STEPS.REVIEW);
    }
  };

  // Step: Review & Create
  const handleCreateTerrain = async () => {
    if (!spotData?.spot_id) {
      setError('Identifiant de spot manquant');
      return;
    }

    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      console.log('Session:', !!session, 'Token:', session?.access_token?.substring(0, 20));
      console.log('Payload:', { name: formData.name, lat: location?.lat, lng: location?.lng, city: formData.city, photos: photos.length });
      if (!session?.access_token) throw new Error('Session expirée, reconnecte-toi');
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/createPlayground`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({
          name: formData.name,
          lat: location.lat,
          lng: location.lng,
          address: formData.address,
          city: formData.city,
          zip: formData.zip,
          type: formData.type,
          photo_urls: photos,
          user_lat: location.lat,
          user_lng: location.lng
        })
      });
      const result = await response.json();
      console.log('createPlayground response:', response.status, result);
      if (!response.ok) throw new Error(result.error || 'Erreur lors de la création du terrain');

      setCreatedTerrain(result.playground);
      setCanAddMore(true);
      
      // Invalidate challenge-related queries to refresh the challenges page
      // Use exact keys to match the Challenges page queries
      queryClient.invalidateQueries({ 
        queryKey: ['userPlaygrounds']
      });
      queryClient.invalidateQueries({ 
        queryKey: ['userProgress'] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['relevantChallenges']
      });
      queryClient.invalidateQueries({ 
        queryKey: ['userTickets'] 
      });
      
      setStep(STEPS.POST_CREATE);
    } catch (err) {
      if (err.response?.data?.error?.includes('limite')) {
        setError(err.response.data.error);
      } else {
        setError(err.message || 'Échec de la création du terrain');
      }
    } finally {
      setLoading(false);
    }
  };

  // Step: Post-Create "Add another?"
  const handleAddAnother = () => {
    // Reset form, keep spot_id, go back to terrain name
    setPhotos([]);
    setFormData({
      name: '',
      address: formData.address,
      city: formData.city,
      zip: formData.zip,
      type: 'outdoor',
      meta_json: {}
    });
    setAiResult(null);
    setError('');
    setStep(STEPS.TERRAIN_NAME);
  };

  const handleFinish = () => {
    queryClient.invalidateQueries({ queryKey: ['playgrounds'] });
    if (onClose) {
      onClose();
    } else {
      navigate('/map');
    }
  };

  // ===== RENDER STEPS =====

  if (step === STEPS.LOCATION) {
    const content = (
      <div className="p-4">
        {locationLoading && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#6E2BFF] border-t-transparent"></div>
              <p className="text-white font-bold text-lg">Recherche de localisation...</p>
            </div>
          </div>
        )}
        <Button
          variant="ghost"
          onClick={onClose ? onClose : () => navigate('/map')}
          className="text-white hover:text-[#6E2BFF] mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
        <div className="max-w-md mx-auto space-y-6">
            <h1 className="text-lg font-black text-white uppercase tracking-wide text-center">
              Ajouter un terrain
            </h1>

          <Card className="p-6 bg-[#13151D] border-[#6E2BFF]">
            <Input
              placeholder="Adresse"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="bg-[#1B1E28] border-[#6E2BFF] text-white mb-4"
            />

            <div className="grid grid-cols-2 gap-3 mb-4">
              <Input
                placeholder="Ville"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="bg-[#1B1E28] border-[#6E2BFF] text-white"
              />
              <Input
                placeholder="Code postal"
                value={formData.zip}
                onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                className="bg-[#1B1E28] border-[#6E2BFF] text-white"
              />
            </div>

            {/* Location picker */}
            <div className="mb-4">
              <label className="block text-sm text-[#B3B6C2] mb-2">
                <MapPin className="inline w-4 h-4 mr-1" />
                Localisation (Cliquer pour définir)
              </label>
              <Button
                variant="outline"
                className="w-full border-[#6E2BFF] text-[#6E2BFF] hover:bg-[#6E2BFF]/10 hover:text-white"
                onClick={async () => {
                  setLocationLoading(true);
                  try {
                    const location = await getLocation({ 
                      useCache: true, 
                      timeout: 3000 
                    });
                    setLocation({ lat: location.lat, lng: location.lng });

                    // Fetch city via Mapbox reverse geocoding //
                    const token = import.meta.env.VITE_MAPBOX_TOKEN;
                    const response = await fetch(
                      `https://api.mapbox.com/geocoding/v5/mapbox.places/${location.lng},${location.lat}.json?access_token=${token}`
                    );
                    const data = await response.json();
                    if (data.features && data.features.length > 0) {
                      const place = data.features.find(f => f.place_type.includes('place'));
                      if (place) {
                        setFormData(prev => ({ ...prev, city: place.text }));
                      }
                    }
                  } catch (err) {
                    console.error('Failed to get location:', err);
                    setError('Impossible de récupérer votre position');
                  } finally {
                    setLocationLoading(false);
                  }
                }}
              >
                {location ? `${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}` : 'Obtenir ma position'}
              </Button>
            </div>

            {error && (
              <div className="mt-4 p-3 bg-red-500/20 border border-red-500 text-red-400 rounded text-sm">
                {error}
              </div>
            )}

            <Button
              onClick={handleLocationSubmit}
              disabled={!location}
              className="w-full mt-4 bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white font-bold"
            >
              Suivant
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            </Card>
        </div>
      </div>
    );

    return content;
  }

  if (step === STEPS.TERRAIN_NAME) {
    const content = (
      <div className="p-4">
        <div className="max-w-md mx-auto space-y-6">
          <h1 className="text-lg font-black text-white uppercase tracking-wide">
            Nom du terrain
          </h1>

          <Card className="p-6 bg-[#13151D] border-[#6E2BFF]">
            <Input
              placeholder="Nom du terrain"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              autoFocus
              className="bg-[#1B1E28] border-[#6E2BFF] text-white mb-4"
            />

            <div className="mb-4">
              <label className="block text-sm text-[#B3B6C2] mb-2">Type</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="w-full bg-[#1B1E28] border border-[#6E2BFF] text-white rounded p-2"
              >
                <option value="outdoor">Outdoor</option>
                <option value="indoor">Indoor</option>
                <option value="street">Street</option>
              </select>
            </div>



            {error && (
              <div className="mb-4 p-3 bg-red-500/20 border border-red-500 text-red-400 rounded text-sm">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Button
                onClick={() => setStep(STEPS.PHOTOS)}
                disabled={!formData.name}
                className="w-full bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white font-bold"
              >
                Suivant
              </Button>
              <Button
                variant="outline"
                className="w-full border-[#6E2BFF] text-[#6E2BFF]"
                onClick={() => setStep(STEPS.LOCATION)}
              >
                Retour
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );

    return content;
  }

  if (step === STEPS.SPOT_CHECK) {
    const isFull = spotData?.courts_count >= 4;

    const content = (
      <div className="p-4">
        <div className="max-w-md mx-auto">
          <Card className="p-8 bg-[#13151D] border-l-4 border-[#6E2BFF] space-y-4">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-[#6E2BFF] mx-auto mb-3" />
              <h2 className="text-lg font-black text-white uppercase">Ce lieu existe déjà</h2>
            </div>

            <p className="text-center text-[#B3B6C2]">
              Il y a déjà <strong className="text-white">{spotData?.courts_count}</strong> terrain(s) à cet emplacement.
            </p>

            {spotData?.courts && spotData.courts.length > 0 && (
              <div className="space-y-2 border-t border-[#1B1E28] pt-4">
                {spotData.courts.map((court) => (
                  <div key={court.id} className="text-sm text-[#B3B6C2]">
                    • {court.name}
                  </div>
                ))}
              </div>
            )}

            {isFull && (
              <div className="p-4 bg-red-500/20 border border-red-500 rounded text-red-300 text-sm">
                Ce lieu contient déjà 4 terrains. Pour garantir la lisibilité et l'équité, aucun terrain supplémentaire ne peut être ajouté à cet emplacement.
              </div>
            )}

            <div className="space-y-2 pt-2">
              {!isFull && (
                <Button
                  onClick={handleSpotCheckContinue}
                  className="w-full bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white font-bold"
                >
                  Ajouter un terrain ici
                </Button>
              )}
              <Button
                variant="outline"
                className="w-full border-[#6E2BFF] text-[#6E2BFF]"
                onClick={() => setStep(STEPS.LOCATION)}
              >
                Annuler
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );

    return content;
  }

  if (step === STEPS.PHOTOS) {
    const content = (
      <div className="p-4">
        {loading && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#6E2BFF] border-t-transparent"></div>
              <p className="text-white font-bold text-lg">{aiLoading ? 'Analyse en cours...' : 'Upload en cours...'}</p>
            </div>
          </div>
        )}
        <div className="max-w-md mx-auto space-y-6">
          <h1 className="text-lg font-black text-white uppercase tracking-wide">
            Photos du terrain
          </h1>

          <Card className="p-6 bg-[#13151D] border-[#6E2BFF]">
            <p className="text-[#B3B6C2] text-sm mb-4">
              {spotData?.courts_count > 0
                ? 'Upload 2 photos minimum pour l\'IA. 3e photo en option si incertain.'
                : 'Upload 2 photos minimum'}
            </p>

            <div className="space-y-3 mb-6">
              {photos.map((photoUrl, idx) => (
                <div key={idx} className="relative">
                  <img
                    src={photoUrl}
                    alt={`Photo ${idx + 1}`}
                    className="w-full h-32 object-cover rounded border border-[#6E2BFF]"
                  />
                  <button
                    onClick={() => removePhoto(idx)}
                    className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 rounded-full p-1"
                  >
                    <X className="w-4 h-4 text-white" />
                  </button>
                </div>
              ))}
            </div>

            {photos.length < 3 && (
              <div className="space-y-3">
                <p className="text-[#B3B6C2] text-xs text-center">
                  {photos.length === 0
                    ? 'Ajoutez vos photos'
                    : `${3 - photos.length} photo(s) supplémentaire(s) autorisée(s)`}
                </p>

                {/* Prendre une photo avec la caméra */}
                <label className="block">
                  <div className="border-2 border-[#6E2BFF] rounded-lg p-4 text-center cursor-pointer hover:bg-[#6E2BFF]/10 transition bg-[#6E2BFF]/5">
                    <Camera className="w-6 h-6 text-[#6E2BFF] mx-auto mb-2" />
                    <p className="text-white text-sm font-bold">Prendre une photo</p>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handlePhotoUpload}
                    disabled={loading || photos.length >= 3}
                    className="hidden"
                  />
                </label>

                {/* Choisir depuis la galerie */}
                <label className="block">
                  <div className="border-2 border-dashed border-[#6E2BFF] rounded-lg p-4 text-center cursor-pointer hover:bg-[#6E2BFF]/10 transition">
                    <Image className="w-6 h-6 text-[#6E2BFF] mx-auto mb-2" />
                    <p className="text-[#B3B6C2] text-sm">Choisir depuis la galerie</p>
                  </div>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    disabled={loading || photos.length >= 3}
                    className="hidden"
                  />
                </label>
              </div>
            )}

            {error && (
              <div className="mt-4 p-3 bg-red-500/20 border border-red-500 text-red-400 rounded text-sm">
                {error}
              </div>
            )}

            <div className="mt-6 space-y-2">
              <Button
                onClick={handlePhotosSubmit}
                disabled={loading || photos.length < 2}
                className="w-full bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white font-bold"
              >
                {aiLoading ? 'Analyse...' : 'Suivant'}
              </Button>
              <Button
                variant="outline"
                className="w-full border-[#6E2BFF] text-[#6E2BFF]"
                onClick={() => setStep(STEPS.TERRAIN_NAME)}
              >
                Retour
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );

    return content;
  }

  if (step === STEPS.AI_ANALYSIS) {
    const content = (
      <div className="p-4">
        <div className="max-w-md mx-auto">
          <Card className="p-8 bg-[#13151D] border-l-4 border-[#6E2BFF] space-y-4">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-[#6E2BFF] mx-auto mb-3" />
              <h2 className="text-lg font-black text-white uppercase">Terrain déjà existant ?</h2>
            </div>

            <p className="text-center text-[#B3B6C2]">
              Ces photos ressemblent fortement à un terrain déjà présent.
            </p>

            <div className="space-y-2 pt-4">
               <Button
                 className="w-full bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white font-bold"
                 onClick={() => {
                   if (onClose) onClose();
                 }}
               >
                 Voir les terrains existants
               </Button>
              <Button
                variant="outline"
                className="w-full border-[#6E2BFF] text-[#6E2BFF]"
                onClick={() => setStep(STEPS.REVIEW)}
              >
                C'est un nouveau terrain
              </Button>
              <Button
                variant="outline"
                className="w-full border-[#6E2BFF] text-[#6E2BFF]"
                onClick={() => setStep(STEPS.PHOTOS)}
              >
                Annuler
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );

    return content;
  }

  if (step === STEPS.AI_UNCERTAIN) {
    const content = (
      <div className="p-4">
        <div className="max-w-md mx-auto">
          <Card className="p-8 bg-[#13151D] border-l-4 border-yellow-500 space-y-4">
            <div className="text-center">
              <HelpCircle className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
              <h2 className="text-lg font-black text-white uppercase">Nous ne sommes pas sûrs</h2>
            </div>

            <p className="text-center text-[#B3B6C2]">
              {photos.length < 3
                ? 'Une 3e photo (extérieur/repère) nous aiderait.'
                : 'Même après l\'analyse, nous ne pouvons pas décider.'}
            </p>

            <div className="space-y-2 pt-4">
              {photos.length < 3 ? (
                <>
                  <Button
                    className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-bold"
                    onClick={handleAiUncertainAddPhoto}
                  >
                    Ajouter une 3e photo
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full border-yellow-500 text-yellow-500"
                    onClick={() => handleAiUncertainUserChoice('DIFFERENT')}
                  >
                    C'est un terrain différent
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-sm text-[#B3B6C2] text-center border-t border-[#1B1E28] pt-4">
                    Même terrain ou terrain différent ?
                  </p>
                  <Button
                    className="w-full bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white font-bold"
                    onClick={() => handleAiUncertainUserChoice('SAME')}
                  >
                    Même terrain
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full border-[#6E2BFF] text-[#6E2BFF]"
                    onClick={() => handleAiUncertainUserChoice('DIFFERENT')}
                  >
                    Terrain différent
                  </Button>
                </>
              )}
            </div>
          </Card>
        </div>
      </div>
    );

    return content;
  }

  if (step === STEPS.REVIEW) {
    const content = (
      <div className="p-4">
        <div className="max-w-md mx-auto space-y-6">
          <h1 className="text-lg font-black text-white uppercase tracking-wide">
            Vérifier le terrain
          </h1>

          <Card className="p-6 bg-[#13151D] border-[#6E2BFF] space-y-4">
            <div className="space-y-2">
              <p className="text-[#B3B6C2] text-sm">Nom</p>
              <p className="text-white font-bold text-lg">{formData.name}</p>
            </div>

            <div className="space-y-2">
              <p className="text-[#B3B6C2] text-sm">Localisation</p>
              <p className="text-white font-bold">
                {formData.address && `${formData.address}, `}{formData.city}
              </p>
            </div>

            <div className="space-y-2 border-t border-[#1B1E28] pt-4">
              <p className="text-[#B3B6C2] text-sm">Photos</p>
              <div className="grid grid-cols-2 gap-2">
                {photos.map((url, idx) => (
                  <img key={idx} src={url} alt={`Photo ${idx + 1}`} className="w-full h-20 object-cover rounded" />
                ))}
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-500/20 border border-red-500 text-red-400 rounded text-sm">
                {error}
              </div>
            )}

            <div className="space-y-2 pt-2">
              <Button
                onClick={handleCreateTerrain}
                disabled={loading}
                className="w-full bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white font-bold"
              >
                {loading ? 'Création...' : 'Créer le terrain'}
              </Button>
              <Button
                variant="outline"
                className="w-full border-[#6E2BFF] text-[#6E2BFF]"
                onClick={() => setStep(STEPS.PHOTOS)}
              >
                Retour
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );

    return content;
  }

  if (step === STEPS.POST_CREATE) {
    const content = (
      <div className="p-4">
        <div className="max-w-md mx-auto">
          <Card className="p-8 bg-[#13151D] border-l-4 border-green-500 space-y-4">
            <div className="text-center">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <h2 className="text-lg font-black text-white uppercase">Terrain créé !</h2>
            </div>

            <p className="text-center text-[#B3B6C2]">
              {createdTerrain?.name} a été ajouté à cet emplacement.
            </p>

            <p className="text-center text-sm text-[#B3B6C2] border-t border-[#1B1E28] pt-4">
              Y a-t-il d'autres terrains à cet emplacement ?
            </p>

            <div className="space-y-2 pt-2">
              <Button
                onClick={handleAddAnother}
                className="w-full bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white font-bold flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Ajouter un autre terrain ici
              </Button>
              <Button
                onClick={handleFinish}
                variant="outline"
                className="w-full border-[#6E2BFF] text-[#6E2BFF]"
              >
                Terminer
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );

    return content;
  }

  return (
    <>
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#6E2BFF] border-t-transparent"></div>
            <p className="text-white font-bold text-lg">
              {step === STEPS.PHOTOS && 'Upload en cours...'}
              {step === STEPS.LOCATION && 'Détection...'}
              {aiLoading && 'Analyse AI en cours...'}
              {step === STEPS.REVIEW && 'Création...'}
            </p>
          </div>
        </div>
      )}
      {null}
    </>
  );
}